<template>
  <div id="pageReporte2">
    <v-container fluid>
      <v-row>
        <v-col>
          <v-card class="elevation-2">
            <v-toolbar color="primary" dark flat class="report-toolbar">
              <v-icon class="mr-2">mdi-account-check-outline</v-icon>
              <v-toolbar-title>Uso real de la herramienta</v-toolbar-title>
              <v-spacer></v-spacer>
              <report-filters :fields="['period', 'location_id', 'condition', 'type', 'responsible_id']" @change="onFilterChange"></report-filters>
              <v-btn outlined :loading="loadingExport" @click="downloadExcel">
                <v-icon left small>mdi-microsoft-excel</v-icon>
                <span class="d-none d-sm-inline">Descargar Excel</span>
              </v-btn>
              <v-btn outlined class="ml-2" :loading="loadingPdf" @click="downloadPdf(2)">
                <v-icon left small>mdi-file-pdf-box</v-icon>
                <span class="d-none d-sm-inline">Descargar PDF</span>
              </v-btn>
            </v-toolbar>

            <v-card-text class="pt-6">
              <div class="text-body-2 grey--text text--darken-1 mb-6">
                Sobre los movimientos y cesiones <strong>Completados</strong> del periodo: qué usuarios ejecutaron la entrega en cada
                ubicación de origen y la recepción en cada ubicación de destino. Quien lee el report identifica qué personas son
                de la ubicación y cuáles están cerrando operaciones que no les corresponden.
              </div>

              <v-progress-linear v-if="loadingPreview" indeterminate color="primary" class="mb-6"></v-progress-linear>

              <div v-else>
                <v-alert v-if="!previewItems.length" type="info" text dense>
                  No hay datos para mostrar
                </v-alert>

                <template v-else>
                  <!-- KPIs -->
                  <div class="kpi-grid mb-6">
                    <div class="kpi-card">
                      <v-icon size="30" color="green darken-1">mdi-check-circle-outline</v-icon>
                      <div class="kpi-number">{{ kpis.total }}</div>
                      <div class="kpi-label">Completados analizados</div>
                    </div>
                    <div class="kpi-card">
                      <v-icon size="30" color="blue darken-2">mdi-map-marker-outline</v-icon>
                      <div class="kpi-number">{{ kpis.locations }}</div>
                      <div class="kpi-label">Ubicaciones</div>
                    </div>
                    <div class="kpi-card">
                      <v-icon size="30" color="teal darken-1">mdi-account-group-outline</v-icon>
                      <div class="kpi-number">{{ kpis.users }}</div>
                      <div class="kpi-label">Usuarios que ejecutan</div>
                    </div>
                    <div class="kpi-card">
                      <v-icon size="30" color="purple darken-1">mdi-timer-outline</v-icon>
                      <div class="kpi-number">{{ kpis.avgDays }} d</div>
                      <div class="kpi-label">Tiempo medio entrega → recepción</div>
                    </div>
                  </div>

                  <!-- Señal a vigilar -->
                  <v-alert v-if="alertUser" type="info" text dense border="left" class="mb-6">
                    <v-icon left small>mdi-lightbulb-on-outline</v-icon>
                    Señal a vigilar: un mismo usuario que aparece entregando o recepcionando en muchas ubicaciones distintas.
                    <strong>{{ alertUser.name }}</strong> actúa en las {{ alertUser.locations.length }} ubicaciones y hace el {{ alertUser.receptionPct }} % de las recepciones.
                  </v-alert>

                  <!-- Por ubicación -->
                  <div class="report-section-title">
                    <v-icon small color="primary" class="mr-1">mdi-map-marker-outline</v-icon>
                    Por ubicación
                  </div>
                  <v-simple-table class="report-table mb-2" dense>
                    <thead>
                      <tr>
                        <th>Ubicación</th>
                        <th>Entregas (como origen)</th>
                        <th class="text-center">Nº</th>
                        <th>Recepciones (como destino)</th>
                        <th class="text-center">Nº</th>
                        <th class="text-right">T. medio recepción</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in byLocation" :key="row.location">
                        <td class="font-weight-medium">{{ row.location }}</td>
                        <td style="min-width: 220px;">
                          <div class="stacked-bar">
                            <v-tooltip top v-for="segment in row.deliverySegments" :key="'d-' + segment.name">
                              <template v-slot:activator="{ on, attrs }">
                                <div
                                  class="stacked-bar-segment"
                                  :style="{ width: segment.pct + '%', backgroundColor: segmentColor(segment.name) }"
                                  v-bind="attrs"
                                  v-on="on"
                                >
                                  <span v-if="segment.pct >= 12">{{ segment.name }} {{ segment.pct }}%</span>
                                </div>
                              </template>
                              <span>{{ segment.name }}: {{ segment.count }} de {{ row.deliveryTotal }} entregas ({{ segment.pct }}%)</span>
                            </v-tooltip>
                          </div>
                        </td>
                        <td class="text-center">{{ row.deliveryTotal }}</td>
                        <td style="min-width: 220px;">
                          <div class="stacked-bar">
                            <v-tooltip top v-for="segment in row.receptionSegments" :key="'r-' + segment.name">
                              <template v-slot:activator="{ on, attrs }">
                                <div
                                  class="stacked-bar-segment"
                                  :style="{ width: segment.pct + '%', backgroundColor: segmentColor(segment.name) }"
                                  v-bind="attrs"
                                  v-on="on"
                                >
                                  <span v-if="segment.pct >= 12">{{ segment.name }} {{ segment.pct }}%</span>
                                </div>
                              </template>
                              <span>{{ segment.name }}: {{ segment.count }} de {{ row.receptionTotal }} recepciones ({{ segment.pct }}%)</span>
                            </v-tooltip>
                          </div>
                        </td>
                        <td class="text-center">{{ row.receptionTotal }}</td>
                        <td class="text-right">{{ row.avgReceptionDays }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>

                  <div class="legend mb-8">
                    <span v-for="user in byUser" :key="'legend-' + user.name" class="legend-item">
                      <span class="legend-dot" :style="{ backgroundColor: segmentColor(user.name) }"></span>
                      {{ user.name }}
                    </span>
                  </div>

                  <!-- Por usuario -->
                  <div class="report-section-title">
                    <v-icon small color="primary" class="mr-1">mdi-account-multiple-outline</v-icon>
                    Por usuario
                  </div>
                  <v-simple-table class="report-table" dense>
                    <thead>
                      <tr>
                        <th>Usuario</th>
                        <th class="text-center">Entregas</th>
                        <th class="text-center">Recepciones</th>
                        <th class="text-center">Total acciones</th>
                        <th class="text-center">Nº ubicaciones en las que actúa</th>
                        <th>Ubicaciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="user in byUser" :key="user.name">
                        <td class="font-weight-medium">{{ user.name }}</td>
                        <td class="text-center">{{ user.deliveries }}</td>
                        <td class="text-center">{{ user.receptions }}</td>
                        <td class="text-center font-weight-bold">{{ user.total }}</td>
                        <td class="text-center">
                          <span class="bucket-pill" :class="user.locations.length > 1 ? 'bucket-3' : 'bucket-0'">{{ user.locations.length }}</span>
                        </td>
                        <td>{{ user.locations.join(', ') }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </template>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import ReportService from '../../services/ReportService'
import errorHandler from '../../mixins/errorHandler'
import reportPdf from '../../mixins/reportPdf'
import ReportFilters from '../../components/ReportFilters'

const COLOR_PALETTE = ['#E53935', '#1E88E5', '#00897B', '#8E24AA', '#FB8C00', '#3949AB', '#00ACC1', '#6D4C41']
const MISSING_COLOR = '#BDBDBD'

export default {
  name: 'Reporte2',

  components: { ReportFilters },

  mixins: [errorHandler, reportPdf],

  data: () => ({
    params: { period: 'this_month' },
    previewItems: [],
    loadingPreview: true,
    loadingExport: false
  }),

  computed: {
    kpis () {
      const total = this.previewItems.length
      const locations = new Set()
      const users = new Set()
      let daysSum = 0
      let daysCount = 0

      this.previewItems.forEach(row => {
        if (row.origin) locations.add(row.origin)
        if (row.destination) locations.add(row.destination)
        if (row.delivered_by) users.add(row.delivered_by)
        if (row.received_by) users.add(row.received_by)
        if (row.days !== null && row.days !== undefined) {
          daysSum += row.days
          daysCount++
        }
      })

      return {
        total,
        locations: locations.size,
        users: users.size,
        avgDays: daysCount ? (daysSum / daysCount).toFixed(1).replace('.', ',') : '—'
      }
    },

    byLocation () {
      const locations = new Set()
      this.previewItems.forEach(row => {
        if (row.origin) locations.add(row.origin)
        if (row.destination) locations.add(row.destination)
      })

      const rows = Array.from(locations).map(location => {
        const deliveryRows = this.previewItems.filter(row => row.origin === location)
        const receptionRows = this.previewItems.filter(row => row.destination === location)
        const daysValues = receptionRows.filter(row => row.days !== null && row.days !== undefined).map(row => row.days)
        const avgDays = daysValues.length ? (daysValues.reduce((a, b) => a + b, 0) / daysValues.length).toFixed(1).replace('.', ',') + ' d' : '—'

        return {
          location,
          deliverySegments: this.buildSegments(deliveryRows, 'delivered_by'),
          deliveryTotal: deliveryRows.length,
          receptionSegments: this.buildSegments(receptionRows, 'received_by'),
          receptionTotal: receptionRows.length,
          avgReceptionDays: avgDays,
          sortWeight: deliveryRows.length + receptionRows.length
        }
      })

      return rows.sort((a, b) => b.sortWeight - a.sortWeight)
    },

    byUser () {
      const users = new Set()
      this.previewItems.forEach(row => {
        if (row.delivered_by) users.add(row.delivered_by)
        if (row.received_by) users.add(row.received_by)
      })

      const rows = Array.from(users).map(name => {
        const deliveries = this.previewItems.filter(row => row.delivered_by === name)
        const receptions = this.previewItems.filter(row => row.received_by === name)
        const locations = new Set()
        deliveries.forEach(row => { if (row.origin) locations.add(row.origin) })
        receptions.forEach(row => { if (row.destination) locations.add(row.destination) })

        return {
          name,
          deliveries: deliveries.length,
          receptions: receptions.length,
          total: deliveries.length + receptions.length,
          locations: Array.from(locations)
        }
      })

      return rows.sort((a, b) => b.total - a.total)
    },

    userColorMap () {
      const map = {}
      this.byUser.forEach((user, index) => {
        map[user.name] = COLOR_PALETTE[index % COLOR_PALETTE.length]
      })
      return map
    },

    alertUser () {
      if (!this.byUser.length) return null
      const candidate = this.byUser.reduce((max, user) => (
        !max || user.locations.length > max.locations.length ? user : max
      ), null)

      if (!candidate || candidate.locations.length <= 1) return null

      const totalReceptions = this.previewItems.length
      const receptionPct = totalReceptions ? Math.round((candidate.receptions / totalReceptions) * 100) : 0

      return { name: candidate.name, locations: candidate.locations, receptionPct }
    }
  },

  methods: {
    onFilterChange (params) {
      this.params = params
      this.loadPreview()
    },

    buildSegments (rows, field) {
      const total = rows.length
      if (!total) return []

      const counts = {}
      rows.forEach(row => {
        const who = row[field]
        if (who) counts[who] = (counts[who] || 0) + 1
      })

      const segments = Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .map(([name, count]) => ({ name, count, pct: Math.round((count / total) * 100) }))

      const registered = Object.values(counts).reduce((a, b) => a + b, 0)
      const missing = total - registered
      if (missing > 0) {
        segments.push({ name: 'Sin registrar', count: missing, pct: Math.round((missing / total) * 100) })
      }

      return segments
    },

    segmentColor (name) {
      if (name === 'Sin registrar') return MISSING_COLOR
      return this.userColorMap[name] || MISSING_COLOR
    },

    async loadPreview () {
      try {
        this.loadingPreview = true
        const { data } = await ReportService.getReport2(this.params)
        this.previewItems = data.data || []
      } catch (error) {
        this.handleApiError(error, 'Error al cargar los datos del reporte')
      } finally {
        this.loadingPreview = false
      }
    },

    async downloadExcel () {
      try {
        this.loadingExport = true
        const response = await ReportService.downloadReport2(this.params)
        const blob = response.data

        if (!blob || blob.size === 0) {
          throw new Error('El archivo descargado está vacío')
        }

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
        link.download = `reporte2_${timestamp}.xlsx`
        link.style.display = 'none'

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        this.$toast.success('Reporte descargado correctamente')
      } catch (error) {
        this.handleApiError(error, 'Error al descargar el reporte')
      } finally {
        this.loadingExport = false
      }
    }
  },

  created () {
    this.loadPreview()
  }
}
</script>

<style scoped>
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
}

.kpi-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px 12px;
  text-align: center;
}

.kpi-number {
  font-size: 30px;
  font-weight: 600;
  line-height: 1.3;
  margin-top: 4px;
}

.kpi-label {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.7);
}

.report-section-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 12px;
}

.report-table {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.report-table >>> thead th {
  background-color: #fafafa;
  font-weight: 600 !important;
}

.stacked-bar {
  display: flex;
  width: 100%;
  height: 26px;
  border-radius: 4px;
  overflow: hidden;
  background-color: #f0f0f0;
}

.stacked-bar-segment {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.7);
}

.legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
}

.bucket-pill {
  display: inline-block;
  min-width: 28px;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;
  text-align: center;
}

.bucket-0 {
  background-color: #E8F5E9;
  color: #2E7D32;
}

.bucket-3 {
  background-color: #FFEBEE;
  color: #C62828;
}
</style>
